import React, { useState, useEffect } from "react";
import "./../../../assets/css/mentor/Mentorwizard.css";

/* ══════════════════════════════════════════
   STEP DATA
══════════════════════════════════════════ */
const STEP_1_OPTIONS = [
  {
    id: "beginner",
    label: "Beginner",
    desc: "Just starting my tech journey",
    icon: "🌱",
  },
  {
    id: "intermediate",
    label: "Intermediate",
    desc: "Building real projects, hitting walls",
    icon: "⚡",
  },
  {
    id: "advanced",
    label: "Advanced",
    desc: "Scaling up, leveling my career",
    icon: "🚀",
  },
];

const STEP_2_OPTIONS = [
  { id: "frontend", label: "Frontend", icon: "🎨" },
  { id: "backend", label: "Backend", icon: "⚙️" },
  { id: "fullstack", label: "Full-stack", icon: "🔗" },
  { id: "system_design", label: "System Design", icon: "🏗️" },
  { id: "career_switch", label: "Career switch", icon: "🔄" },
  { id: "interview_prep", label: "Interview prep", icon: "🎯" },
];

const STEP_3_OPTIONS = [
  {
    id: "code_reviews",
    label: "Code reviews",
    desc: "Line-by-line feedback on my work",
    icon: "🔍",
  },
  {
    id: "roadmap",
    label: "Roadmap & accountability",
    desc: "Clear path + keep me on track",
    icon: "🗺️",
  },
  {
    id: "career",
    label: "Career guidance",
    desc: "Job hunt, salary & growth strategy",
    icon: "💼",
  },
  {
    id: "debugging",
    label: "Debugging support",
    desc: "Help when I'm stuck on hard problems",
    icon: "🐛",
  },
];

const STEP_4_TIME_OPTIONS = [
  {
    id: "2_3",
    label: "2–3 hrs / week",
    desc: "Light pace, steady progress",
    icon: "🕐",
  },
  {
    id: "4_6",
    label: "4–6 hrs / week",
    desc: "Balanced commitment",
    icon: "🕓",
  },
  {
    id: "7plus",
    label: "7+ hrs / week",
    desc: "Intensive, fast track",
    icon: "🕗",
  },
];

const STEP_4_INTERACTION = [
  {
    id: "async",
    label: "Async",
    desc: "Messages, Loom videos, reviews",
    icon: "💬",
  },
  {
    id: "live",
    label: "Live sessions",
    desc: "Video calls, pair programming",
    icon: "📹",
  },
];

const STEP_5_OPTIONS = [
  {
    id: "startup",
    label: "Startup",
    desc: "Moved fast, wore many hats",
    icon: "🔥",
  },
  {
    id: "enterprise",
    label: "Enterprise / Big tech",
    desc: "FAANG or large-scale systems",
    icon: "🏢",
  },
  {
    id: "agency",
    label: "Agency / Freelance",
    desc: "Client work, diverse projects",
    icon: "🎨",
  },
  {
    id: "any",
    label: "Doesn't matter",
    desc: "Just find me the best fit",
    icon: "✨",
  },
];

const STEP_TITLES = [
  "Your Level",
  "Goals",
  "Help Style",
  "Commitment",
  "Mentor Fit",
];

/* ══════════════════════════════════════════
   MENTOR DATA
══════════════════════════════════════════ */
const MENTORS = [
  {
    name: "Aisha Rahman",
    initials: "AR",
    role: "Senior Full-Stack Engineer",
    company: "Stripe",
    match: "Aligns with your full-stack goal and structured learning style",
    tags: ["React", "Node.js", "System Design", "AWS"],
    availability: "available",
    availText: "Available now",
    rating: "4.97",
    sessions: "142",
  },
  {
    name: "Carlos Mendez",
    initials: "CM",
    role: "Staff Engineer",
    company: "Notion",
    match: "Specializes in career transitions into senior-level roles",
    tags: ["Frontend", "TypeScript", "Career Coaching", "Open Source"],
    availability: "limited",
    availText: "2 spots left",
    rating: "4.92",
    sessions: "89",
  },
  {
    name: "Priya Sharma",
    initials: "PS",
    role: "Engineering Manager",
    company: "Razorpay",
    match: "Startup background with deep roadmap & mentoring experience",
    tags: ["Backend", "Python", "Leadership", "Interview Prep"],
    availability: "available",
    availText: "Available now",
    rating: "4.95",
    sessions: "204",
  },
];

/* ══════════════════════════════════════════
   ICONS (inline SVG helpers)
══════════════════════════════════════════ */
const ChevronRight = () => (
  <svg
    className="arrow-icon"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const ChevronLeft = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const CheckIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3.5"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const StarIcon = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="none"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);
const SparkleIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);
const UsersIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
export default function MentorWizard() {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState("forward");
  const [animKey, setAnimKey] = useState(0);
  const [showLoading, setShowLoading] = useState(false);
  const [loadStep, setLoadStep] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [answers, setAnswers] = useState({
    level: null,
    goals: [],
    helpType: null,
    time: null,
    interaction: null,
    background: null,
  });

  /* Loading animation sequence */
  useEffect(() => {
    if (!showLoading) return;
    const timers = [];
    timers.push(setTimeout(() => setLoadStep(1), 400));
    timers.push(setTimeout(() => setLoadStep(2), 900));
    timers.push(setTimeout(() => setLoadStep(3), 1500));
    timers.push(
      setTimeout(() => {
        setShowLoading(false);
        setShowResults(true);
      }, 2200),
    );
    return () => timers.forEach(clearTimeout);
  }, [showLoading]);

  const goNext = () => {
    if (step < 5) {
      setDirection("forward");
      setAnimKey((k) => k + 1);
      setStep((s) => s + 1);
    } else {
      setShowLoading(true);
      setLoadStep(0);
    }
  };

  const goBack = () => {
    if (step > 1) {
      setDirection("back");
      setAnimKey((k) => k + 1);
      setStep((s) => s - 1);
    }
  };

  const toggleGoal = (id) => {
    const g = answers.goals;
    setAnswers({
      ...answers,
      goals: g.includes(id)
        ? g.filter((x) => x !== id)
        : g.length < 2
          ? [...g, id]
          : g,
    });
  };

  const canContinue = () => {
    if (step === 1) return !!answers.level;
    if (step === 2) return answers.goals.length > 0;
    if (step === 3) return !!answers.helpType;
    if (step === 4) return !!answers.time;
    if (step === 5) return !!answers.background;
    return false;
  };

  const progressPct = (step / 5) * 100;

  /* ── LOADING SCREEN ── */
  if (showLoading) {
    return (
      <div className="wizard-page">
        <div className="wizard-bg-dots" />
        <BrandBar />
        <div className="wizard-card">
          <div className="matching-screen">
            <div className="matching-orb">🤖</div>
            <h2 className="matching-title">Finding your perfect mentors…</h2>
            <p className="matching-subtitle">
              Our AI is analysing your goals, learning style,
              <br />
              and experience to find the right fit.
            </p>
            <div className="matching-steps">
              {[
                { icon: "📊", label: "Analysing your experience level" },
                { icon: "🎯", label: "Matching goals & learning style" },
                { icon: "✅", label: "Top 3 mentors found!" },
              ].map((s, i) => (
                <div
                  key={i}
                  className={`matching-step${loadStep > i ? " done" : ""}`}
                >
                  <div className="matching-step-icon">
                    {loadStep > i ? "✓" : s.icon}
                  </div>
                  {s.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── RESULT SCREEN ── */
  if (showResults) {
    return (
      <div className="wizard-page">
        <div className="wizard-bg-dots" />
        <BrandBar />
        <div className="wizard-card">
          {/* Progress — complete */}
          <div className="progress-header">
            <div className="progress-meta">
              <span className="step-label">Matching complete</span>
              <span className="step-fraction">5 of 5 ✓</span>
            </div>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: "100%" }} />
            </div>
          </div>

          {/* Header */}
          <div className="result-header">
            <div className="result-sparkles">
              <span className="sparkle">✨</span>
              <span className="sparkle">🎯</span>
              <span className="sparkle">✨</span>
            </div>
            <div className="result-badge">
              <SparkleIcon />
              AI-Powered Match
            </div>
            <h1 className="result-title">
              Here are mentors <em>best matched</em>
              <br />
              for your goal
            </h1>
            <p className="result-subtitle">
              Matched using your goals, experience level, and learning style.
            </p>
            <div className="result-stats-row">
              <div className="result-stat">
                <span className="result-stat-num">98%</span>
                <span className="result-stat-label">Match Score</span>
              </div>
              <div className="result-stat-divider" />
              <div className="result-stat">
                <span className="result-stat-num">3</span>
                <span className="result-stat-label">Top Mentors</span>
              </div>
              <div className="result-stat-divider" />
              <div className="result-stat">
                <span className="result-stat-num">24h</span>
                <span className="result-stat-label">Avg Response</span>
              </div>
            </div>
          </div>

          {/* Mentor Cards */}
          <div className="result-body">
            {MENTORS.map((m, i) => (
              <div className="mentor-card" key={i}>
                {i === 0 && <div className="top-pick-badge">🏆 Top Pick</div>}
                <div className="mentor-avatar-wrap">
                  <div className="mentor-initials">{m.initials}</div>
                </div>
                <div className="mentor-info">
                  <h3>{m.name}</h3>
                  <p className="mentor-role">
                    {m.role}
                    <span className="role-sep">·</span>
                    {m.company}
                  </p>
                  <p className="mentor-match-reason">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      style={{ flexShrink: 0, marginTop: "1px" }}
                    >
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    {m.match}
                  </p>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      marginBottom: "8px",
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "3px",
                        fontSize: "11.5px",
                        fontWeight: "700",
                        color: "#c06b00",
                      }}
                    >
                      <StarIcon />
                      {m.rating}
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        color: "var(--text-muted)",
                        fontWeight: "500",
                      }}
                    >
                      {m.sessions} sessions
                    </span>
                  </div>
                  <div className="skill-tags">
                    {m.tags.map((t) => (
                      <span className="skill-tag" key={t}>
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="mentor-actions">
                  <span className={`availability-badge ${m.availability}`}>
                    <span className="availability-dot" />
                    {m.availText}
                  </span>
                  <button className="btn-view-plan">View plan</button>
                  <button className="btn-request">Request intro</button>
                </div>
              </div>
            ))}
          </div>

          {/* Result Footer */}
          <div className="result-footer">
            <p className="result-footer-note">
              🔒 Free to connect · No credit card needed
            </p>
            <button
              className="btn-restart"
              onClick={() => {
                setShowResults(false);
                setStep(1);
                setAnswers({
                  level: null,
                  goals: [],
                  helpType: null,
                  time: null,
                  interaction: null,
                  background: null,
                });
              }}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <polyline points="1 4 1 10 7 10" />
                <path d="M3.51 15a9 9 0 1 0 .49-4" />
              </svg>
              Retake quiz
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── WIZARD STEPS ── */
  return (
    <div className="wizard-page">
      <div className="wizard-bg-dots" />

      <BrandBar />

      {/* Step bubble nav */}
      <div className="step-bubbles">
        {STEP_TITLES.map((title, i) => {
          const num = i + 1;
          const isActive = num === step;
          const isCompleted = num < step;
          return (
            <div className="step-bubble-item" key={num}>
              <div
                className={`step-bubble${isActive ? " active" : ""}${isCompleted ? " completed" : ""}`}
              >
                <div className="step-bubble-inner">
                  {isCompleted ? <CheckIcon /> : num}
                  <span className="step-bubble-label">{title}</span>
                </div>
              </div>
              {num < 5 && (
                <div
                  className={`step-connector${isCompleted ? " done" : ""}`}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Card */}
      <div className="wizard-card">
        {/* Progress bar */}
        <div className="progress-header">
          <div className="progress-meta">
            <span className="step-label">{STEP_TITLES[step - 1]}</span>
            <span className="step-fraction">Step {step} of 5</span>
          </div>
          <div className="progress-track">
            <div
              className="progress-fill"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* Step content */}
        <div className="step-body" key={animKey}>
          {/* ── STEP 1 ── */}
          {step === 1 && (
            <>
              <h2 className="step-question">
                What best <span>describes you</span> right now?
              </h2>
              <p className="step-helper">
                This helps us match you with the right mentor level.
              </p>
              <div className="option-grid cols-3">
                {STEP_1_OPTIONS.map((opt) => (
                  <OptionCard
                    key={opt.id}
                    {...opt}
                    selected={answers.level === opt.id}
                    onClick={() => setAnswers({ ...answers, level: opt.id })}
                  />
                ))}
              </div>
            </>
          )}

          {/* ── STEP 2 ── */}
          {step === 2 && (
            <>
              <h2 className="step-question">
                What do you want <span>help with?</span>
              </h2>
              <p className="step-helper">
                <span className="helper-pill">✦ Pick up to 2</span>
                Choose what you'd like to focus on.
              </p>
              <div className="option-grid cols-3">
                {STEP_2_OPTIONS.map((opt) => (
                  <OptionCard
                    key={opt.id}
                    {...opt}
                    selected={answers.goals.includes(opt.id)}
                    onClick={() => toggleGoal(opt.id)}
                    disabled={
                      !answers.goals.includes(opt.id) &&
                      answers.goals.length >= 2
                    }
                  />
                ))}
              </div>
            </>
          )}

          {/* ── STEP 3 ── */}
          {step === 3 && (
            <>
              <h2 className="step-question">
                How should your mentor <span>help you?</span>
              </h2>
              <p className="step-helper">
                Pick the style that fits your learning best.
              </p>
              <div className="option-grid cols-2">
                {STEP_3_OPTIONS.map((opt) => (
                  <OptionCard
                    key={opt.id}
                    {...opt}
                    selected={answers.helpType === opt.id}
                    onClick={() => setAnswers({ ...answers, helpType: opt.id })}
                  />
                ))}
              </div>
            </>
          )}

          {/* ── STEP 4 ── */}
          {step === 4 && (
            <>
              <h2 className="step-question">
                How much time can you <span>commit weekly?</span>
              </h2>
              <p className="step-helper">
                Be honest — consistency matters more than hours.
              </p>

              <div className="section-divider">
                <div className="section-divider-line" />
                <span className="section-label">Weekly commitment</span>
                <div className="section-divider-line" />
              </div>

              <div className="option-grid cols-3">
                {STEP_4_TIME_OPTIONS.map((opt) => (
                  <OptionCard
                    key={opt.id}
                    {...opt}
                    selected={answers.time === opt.id}
                    onClick={() => setAnswers({ ...answers, time: opt.id })}
                  />
                ))}
              </div>

              <div className="section-divider">
                <div className="section-divider-line" />
                <span className="section-label">
                  Interaction preference (optional)
                </span>
                <div className="section-divider-line" />
              </div>

              <div className="option-grid cols-2">
                {STEP_4_INTERACTION.map((opt) => (
                  <OptionCard
                    key={opt.id}
                    {...opt}
                    selected={answers.interaction === opt.id}
                    onClick={() =>
                      setAnswers({
                        ...answers,
                        interaction:
                          answers.interaction === opt.id ? null : opt.id,
                      })
                    }
                  />
                ))}
              </div>
            </>
          )}

          {/* ── STEP 5 ── */}
          {step === 5 && (
            <>
              <h2 className="step-question">
                What kind of mentor <span>inspires you?</span>
              </h2>
              <p className="step-helper">
                Background shapes how they think and teach.
              </p>
              <div className="option-grid cols-2">
                {STEP_5_OPTIONS.map((opt) => (
                  <OptionCard
                    key={opt.id}
                    {...opt}
                    selected={answers.background === opt.id}
                    onClick={() =>
                      setAnswers({ ...answers, background: opt.id })
                    }
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="step-footer">
          <div className="footer-left">
            <button className="btn-back" onClick={goBack} disabled={step === 1}>
              <ChevronLeft />
              Back
            </button>
            {!canContinue() && (
              <span className="footer-hint">Make a selection to continue</span>
            )}
          </div>

          <button
            className="btn-continue"
            onClick={goNext}
            disabled={!canContinue()}
          >
            {step === 5 ? "Find My Mentors" : "Continue"}
            <ChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   BRAND BAR
══════════════════════════════════════════ */
function BrandBar() {
  return (
    <div className="brand-bar">
      <div className="brand-left">
        <div className="brand-logo">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
          >
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </div>
        <span className="brand-name">MentorMatch</span>
      </div>
      <div className="brand-tag">
        <span className="brand-tag-dot" />
        AI-Powered Matching
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   OPTION CARD
══════════════════════════════════════════ */
function OptionCard({ icon, label, desc, selected, onClick, disabled }) {
  return (
    <div
      className={[
        "option-card",
        selected ? "selected" : "",
        disabled ? "disabled" : "",
      ]
        .join(" ")
        .trim()}
      onClick={!disabled ? onClick : undefined}
    >
      <div className="option-icon">{icon}</div>
      <div className="option-text">
        <h4>{label}</h4>
        {desc && <p>{desc}</p>}
      </div>
      <div className="selected-check">
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3.5"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
    </div>
  );
}
